import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Label,
  ReferenceArea,
  ReferenceLine,
} from "recharts";
import RotatedYAxisLabel from "../RechartHelpers/RotatedYAxisLabel";
import { DateTickEvery3rdTickWithVerticalDividersBar } from "../RechartHelpers/DateTickEveryXTicksWithVerticalDividersBar";
import BarLabelWithNullZeros from "../RechartHelpers/BarLabelWithNullZeros";
import { ChartStyles } from "../../../pages/customers/pci/Styles/PciChartStyles";
import { GenericRechartsProps } from "../../../types";
import {
  shiftNumberToLongForm,
  shiftNumberToWord,
} from "../RechartHelpers/ShiftNumberTranslator";

/**
 * generate and create bar chart based on given data
 */
export default function WeeklyMultiShiftBarChart({
  title,
  data,
  xLabel,
  yLabel,
  bars,
  width = 1000,
  height = 600,
  useGrid = false,
  referenceAreas = [{} as any],
  referencelines = [{} as any],
  legend,
}: GenericRechartsProps) {
  return (
    <ChartStyles>
      <div className="chartBody">
        <p className="chartTitle">{title}</p>
        <BarChart
          key={title}
          width={width * 0.95}
          height={height}
          data={data || undefined}
          margin={{
            top: 30,
            right: 60,
            left: 5,
            bottom: 30,
          }}
          stackOffset="sign"
        >
          {useGrid ? (
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
          ) : null}
          <XAxis dataKey="shift" tickFormatter={shiftNumberToLongForm}>
            <Label value={xLabel} offset={30} position="bottom" />
          </XAxis>
          <XAxis
            dataKey="date"
            tick={DateTickEvery3rdTickWithVerticalDividersBar({})}
            axisLine={false}
            tickLine={false}
            interval={0}
            xAxisId="dateId"
            scale="auto"
            type="category"
          />

          <YAxis
            type="number"
            orientation="left"
            label={<RotatedYAxisLabel height={400} value={yLabel} />}
          />

          {legend}
          <Tooltip labelFormatter={shiftNumberToWord} />
          {bars?.map(({ key, name, stackId, fill, label, labelPos }) => {
            return (
              <Bar
                key={key}
                dataKey={key}
                name={name}
                stackId={stackId}
                fill={fill}
                isAnimationActive={false}
              >
                {label ? (
                  <LabelList
                    key={key}
                    dataKey={key}
                    content={BarLabelWithNullZeros as any}
                    position={labelPos ? labelPos : "top"}
                  />
                ) : null}
              </Bar>
            );
          })}

          {referencelines.map(
            ({ y, label, stroke, strokeDasharray, alwaysShow, isFront }) => {
              const key = label + y + stroke;
              return (
                <ReferenceLine
                  key={isNaN(key) ? Math.random() * 1000 : key}
                  label={label}
                  y={y}
                  stroke={stroke}
                  ifOverflow={alwaysShow ? "extendDomain" : "hidden"}
                  isFront={isFront}
                  strokeDasharray={strokeDasharray}
                />
              );
            }
          )}
          {referenceAreas.map(
            ({ label, y, x, x2, stroke, strokeDasharray, isFront }) => {
              const key = label + y + x + x2 + stroke;
              return (
                <ReferenceArea
                  label={label}
                  key={isNaN(key) ? Math.random() * 1000 : key}
                  y1={y}
                  y2={y * 1.000001}
                  x1={x}
                  x2={x2}
                  stroke={stroke}
                  isFront={true}
                  strokeDasharray={strokeDasharray}
                />
              );
            }
          )}
        </BarChart>
      </div>
    </ChartStyles>
  );
}
